import { useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Inbox, Loader2, Trash2, Check, X, Search, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminClassifieds() {
  const { authFetch } = useAdmin();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [total, setTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status !== 'all') params.set('status', status);
    const r = await authFetch(`${API}/admin/classifieds?${params.toString()}`);
    if (r.ok) {
      const d = await r.json();
      setItems(d.classifieds || d.items || []);
      setTotal(d.total || (d.classifieds || d.items || []).length);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const changeStatus = async (it, ns) => {
    const r = await authFetch(`${API}/admin/classifieds/${it.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: ns }),
    });
    if (r.ok) { toast.success('Status ažuriran'); load(); } else toast.error('Greška');
  };

  const remove = async (it) => {
    if (!window.confirm(`Obrisati "${it.title}"?`)) return;
    const r = await authFetch(`${API}/admin/classifieds/${it.id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Obrisano'); load(); }
  };

  const filtered = items.filter(i => !q || (i.title || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-4 md:p-6" data-testid="admin-classifieds">
      <div className="flex items-center gap-3 mb-5">
        <Inbox className="text-red-500" size={24} />
        <div>
          <h1 className="text-2xl font-bold">Oglasi</h1>
          <p className="text-sm text-zinc-400">Moderiraj oglasnik (nekretnine, vozila, usluge…).</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        {['all', 'pending', 'active', 'expired', 'rejected'].map(s => (
          <button key={s} onClick={() => setStatus(s)}
                  data-testid={`filter-${s}`}
                  className={`px-3 py-1.5 rounded text-xs font-bold border ${status === s ? 'bg-red-600 border-red-500 text-white' : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-600'}`}>
            {s === 'all' ? 'Svi' : s === 'pending' ? 'Na čekanju' : s === 'active' ? 'Aktivni' : s === 'expired' ? 'Istekli' : 'Odbijeni'}
          </button>
        ))}
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Traži po naslovu…"
                 data-testid="cls-search"
                 className="w-full bg-zinc-900 border border-zinc-800 rounded pl-8 pr-3 py-1.5 text-sm" />
        </div>
      </div>

      {loading ? <Loader2 className="animate-spin text-zinc-500" /> : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-900 text-xs uppercase text-zinc-400">
              <tr>
                <th className="px-3 py-2 text-left">Slika</th>
                <th className="px-3 py-2 text-left">Naslov</th>
                <th className="px-3 py-2 text-left">Kategorija</th>
                <th className="px-3 py-2 text-left">Cijena</th>
                <th className="px-3 py-2 text-left">Lokacija</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2">Akc.</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(it => (
                <tr key={it.id} className="border-t border-zinc-800" data-testid={`cls-row-${it.id}`}>
                  <td className="px-3 py-2">
                    {(it.images || [])[0] ? (
                      <img src={(it.images || [])[0]} alt="" className="w-12 h-12 object-cover rounded" />
                    ) : <div className="w-12 h-12 bg-zinc-800 rounded" />}
                  </td>
                  <td className="px-3 py-2 max-w-[280px] truncate">{it.title}</td>
                  <td className="px-3 py-2 text-zinc-400 text-xs">{it.category}</td>
                  <td className="px-3 py-2 text-emerald-400">{it.price_eur ? `${it.price_eur} €` : '—'}</td>
                  <td className="px-3 py-2 text-zinc-400 text-xs">{it.location}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      it.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' :
                      it.status === 'pending' ? 'bg-amber-500/20 text-amber-300' :
                      'bg-zinc-700 text-zinc-400'
                    }`}>{it.status}</span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {it.status !== 'active' && (
                      <button onClick={() => changeStatus(it, 'active')} data-testid={`approve-${it.id}`}
                              className="p-1 text-emerald-400 hover:text-emerald-300" title="Odobri">
                        <Check size={14} />
                      </button>
                    )}
                    {it.status !== 'rejected' && (
                      <button onClick={() => changeStatus(it, 'rejected')} data-testid={`reject-${it.id}`}
                              className="p-1 text-amber-400 hover:text-amber-300" title="Odbij">
                        <X size={14} />
                      </button>
                    )}
                    <a href={`/oglasnik/${it.id}`} target="_blank" rel="noreferrer"
                       className="inline-block p-1 text-blue-400 hover:text-blue-300" title="Pregled">
                      <Eye size={14} />
                    </a>
                    <button onClick={() => remove(it)} data-testid={`delete-${it.id}`}
                            className="p-1 text-red-400 hover:text-red-300" title="Obriši">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-zinc-500">Nema oglasa.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-zinc-500 mt-3">Ukupno: {total}</p>
    </div>
  );
}
